import { useCallback } from "react";

export const usePermissionLogic = ({
  selectedPermissions,
  onPermissionChange,
}) => {
  // Helper function to get all permission IDs from a node and its children
  const getAllPermissionIds = useCallback((node) => {
    let ids = new Set();

    // Add current node's permissions
    if (node.permissions) {
      node.permissions.forEach((perm) => ids.add(perm?.id));
    }

    // Add children's permissions recursively
    if (node.children) {
      node.children.forEach((child) => {
        const childIds = getAllPermissionIds(child);
        childIds.forEach((id) => ids.add(id));
      });
    }

    return ids;
  }, []);

  // Check if all children are selected
  const areAllChildrenSelected = useCallback((node) => {
    const allPermissionIds = getAllPermissionIds(node);
    return Array.from(allPermissionIds).every((id) =>
      selectedPermissions.has(id)
    );
  }, [selectedPermissions, getAllPermissionIds]);

  // Check if any children are selected
  const areAnyChildrenSelected = useCallback((node) => {
    const allPermissionIds = getAllPermissionIds(node);
    return Array.from(allPermissionIds).some((id) =>
      selectedPermissions.has(id)
    );
  }, [selectedPermissions, getAllPermissionIds]);

  // Handle permission checkbox change
  const handlePermissionChange = useCallback((permissionId, checked) => {
    const newSelected = new Set(selectedPermissions);

    if (checked) {
      newSelected.add(permissionId);
    } else {
      newSelected.delete(permissionId);
    }

    onPermissionChange(newSelected);
  }, [selectedPermissions, onPermissionChange]);

  // Handle parent checkbox change (select/deselect all children)
  const handleParentChange = useCallback((node, checked) => {
    const newSelected = new Set(selectedPermissions);
    const allPermissionIds = getAllPermissionIds(node);

    if (checked) {
      allPermissionIds.forEach((id) => newSelected.add(id));
    } else {
      allPermissionIds.forEach((id) => newSelected.delete(id));
    }

    onPermissionChange(newSelected);
  }, [selectedPermissions, getAllPermissionIds, onPermissionChange]);

  return {
    handlePermissionChange,
    handleParentChange,
    getAllPermissionIds,
    areAllChildrenSelected,
    areAnyChildrenSelected,
  };
};
